import { Box, Chip, Typography, makeStyles } from '@material-ui/core';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import {
  EntityHealth,
  SEVERITY_COLORS,
  SEVERITY_MARKS,
  STATE_COLORS,
  issueSource,
  issueText,
  UNKNOWN_CAUSE,
  STATUS_COLORS,
  healthState,
  STATUS_LABELS,
  healthLinkPath,
  usePlatformHealth,
} from './api';

const useStyles = makeStyles(theme => ({
  counters: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  entity: {
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  head: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  dot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  title: { fontWeight: 600 },
  issue: { marginLeft: 18 },
  note: { color: theme.palette.text.secondary },
}));

const EntityRow = ({ entity }: { entity: EntityHealth }) => {
  const classes = useStyles();
  const name = entity.title ?? entity.entityRef.split('/').pop();
  return (
    <div className={classes.entity}>
      <div className={classes.head}>
        <span
          className={classes.dot}
          style={{ background: STATE_COLORS[healthState(entity)] }}
        />
        <Link to={healthLinkPath(entity)} className={classes.title}>
          {name}
        </Link>
        <Typography variant="caption" color="textSecondary">
          {[
            entity.clusters.length
              ? `${entity.pods.ready}/${
                  entity.pods.live
                } подов готовы · ${entity.clusters.join(', ')}`
              : undefined,
            entity.alerts.rules
              ? `алерты: ${entity.alerts.firing} из ${entity.alerts.rules} сработали`
              : undefined,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </div>
      {entity.status !== 'ok' &&
        !entity.issues.some(i => i.severity !== 'info') && (
          <Typography variant="body2" className={classes.issue}>
            {UNKNOWN_CAUSE}
          </Typography>
        )}
      {entity.issues.map((issue, i) => (
        <Typography key={i} variant="body2" className={classes.issue}>
          <span
            style={{ color: SEVERITY_COLORS[issue.severity], fontWeight: 700 }}
          >
            {SEVERITY_MARKS[issue.severity]}
          </span>{' '}
          {issueText(issue)}
          <Typography component="span" variant="caption" color="textSecondary">
            {' '}
            — {issueSource(issue)}
          </Typography>
        </Typography>
      ))}
    </div>
  );
};

/**
 * Состояние сервисов в Kubernetes по всем кластерам: аварии и предупреждения сверху, затем
 * замечания (например, много выселенных подов). Данные плагина kubernetes-diagnostics.
 */
export const PlatformHealthCard = () => {
  const classes = useStyles();
  const { value, error, loading } = usePlatformHealth();

  const problems = value?.entities.filter(e => e.status !== 'ok') ?? [];
  const notes =
    value?.entities.filter(
      e => e.status === 'ok' && e.issues.some(i => i.severity === 'info'),
    ) ?? [];
  const count = (s: string) =>
    value?.entities.filter(e => e.status === s).length ?? 0;

  return (
    <InfoCard
      title="Здоровье платформы"
      subheader={
        value
          ? `Kubernetes, обновлено ${new Date(
              value.generatedAt,
            ).toLocaleTimeString()}`
          : undefined
      }
      // Путь страницы «Архитектура» (ARCHITECTURE_PATH в home/HomePage, импорт дал бы цикл).
      deepLink={{ title: 'На схеме архитектуры', link: '/architecture' }}
    >
      {loading && !value && <Progress />}
      {error && !value && (
        <Typography color="error">{error.message}</Typography>
      )}
      {value && (
        <>
          <Box className={classes.counters}>
            {(['critical', 'warning', 'ok'] as const).map(s => (
              <Chip
                key={s}
                size="small"
                label={`${STATUS_LABELS[s]}: ${count(s)}`}
                style={{ background: STATUS_COLORS[s], color: '#fff' }}
              />
            ))}
          </Box>
          {value.alertsError && (
            <Typography color="error" variant="body2">
              Алерты Grafana недоступны: {value.alertsError}
            </Typography>
          )}
          {value.clusterErrors.map(c => (
            <Typography key={c.cluster} color="error" variant="body2">
              Кластер {c.cluster} недоступен: {c.error}
            </Typography>
          ))}
          {problems.length === 0 && (
            <Typography className={classes.note}>
              Аварий и предупреждений нет.
            </Typography>
          )}
          {problems.map(e => (
            <EntityRow key={e.entityRef} entity={e} />
          ))}
          {notes.length > 0 && (
            <>
              <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                Замечания
              </Typography>
              {notes.map(e => (
                <EntityRow key={e.entityRef} entity={e} />
              ))}
            </>
          )}
        </>
      )}
    </InfoCard>
  );
};
